import { OrderHistoryReceiptMeal } from '@tovala/browser-apis-combinedapi'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { events } from 'analytics/events'
import { useIsCreatingMealReview } from 'components/core/hooks/reviews'
import { useUser } from 'contexts/user'
import { track } from 'utils/analytics'

import { useAutoCloseAfterSubmit } from './hooks'
import MealRatingCard from './MealRatingCard'
import RatingDialog, { RatingDialogCloseButton } from './RatingDialog'
import RatingOnboardingSubmittedCard from './RatingOnboardingSubmittedCard'
import RatingSubmittedCard from './RatingSubmittedCard'
import {
  getReviewSourceID,
  hasViewedRatingOnboarding,
  markRatingOnboardingViewed,
} from './utils'

export interface RatingDialogCloseResult {
  wasRated: boolean
}

const MealRatingDialog = ({
  meal,
  onClose,
  sourceID,
}: {
  meal: OrderHistoryReceiptMeal
  onClose(result: RatingDialogCloseResult): void
  sourceID?: string
}) => {
  const { pathname } = useLocation()
  const { user } = useUser()
  const reviewSourceID = getReviewSourceID({ pathname, sourceID })
  const [isOnboarding] = useState(() => !hasViewedRatingOnboarding(user.id))

  useEffect(() => {
    if (isOnboarding) {
      markRatingOnboardingViewed(user.id)
    }
  }, [isOnboarding, user.id])

  const [submittedRating, setSubmittedRating] = useState<number | null>(null)
  const isCreatingMealReview = useIsCreatingMealReview(meal.id)
  const wasRated = submittedRating !== null

  useAutoCloseAfterSubmit({
    hasSubmitted: wasRated,
    onAutoClose: () => onClose({ wasRated: true }),
  })

  const handleClose = () => {
    if (wasRated || isCreatingMealReview) {
      onClose({ wasRated: true })
      return
    }

    track(events.ORDERS_MEAL_REVIEW_DISMISSED, {
      meal_id: meal.id,
      source_id: reviewSourceID,
    })
    onClose({ wasRated: false })
  }

  return (
    <RatingDialog onClose={handleClose} size="singleMeal">
      <div className="sticky top-0 z-10 h-14 border-b border-grey-2 bg-white px-6 py-4">
        <div className="absolute right-6 top-1/2 h-6 w-6 -translate-y-1/2">
          <RatingDialogCloseButton onClose={handleClose} />
        </div>
      </div>
      {submittedRating !== null ? (
        isOnboarding ? (
          <RatingOnboardingSubmittedCard rating={submittedRating} />
        ) : (
          <RatingSubmittedCard rating={submittedRating} />
        )
      ) : (
        <MealRatingCard
          isOnboarding={isOnboarding}
          meal={meal}
          onMealReviewCreated={setSubmittedRating}
          sourceID={reviewSourceID}
        />
      )}
    </RatingDialog>
  )
}

export default MealRatingDialog
